import React, { useMemo, useState } from 'react';
import {
  FiPlus, FiEdit2, FiTrash2, FiMove, FiChevronUp, FiChevronDown,
  FiImage, FiEye, FiEyeOff, FiInbox,
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import { Button, Modal, ConfirmDialog, Toggle, Badge } from '../../../components/common';
import CmsField, { renderFaIcon } from './CmsField';
import { useSortable } from './useSortable';
import { ITEM_FIELDS, getItemFieldLabel, getSectionType } from '../sectionTypes';
import { sectionItemService, getApiError } from '../../../services/cmsService';
import { truncate, stripHtml } from '../../../utils/stringUtils';
import './SectionItemsManager.css';

const emptyItem = () => ({
  title: '',
  subtitle: '',
  description: '',
  icon: '',
  image: '',
  value: '',
  linkLabel: '',
  linkUrl: '',
  extra: {},
  sortOrder: 0,
  isActive: true,
});

/**
 * Repeatable children of a section (hero slides, cards, stats, FAQ rows…).
 * Which fields appear is driven entirely by the section type registry.
 */
const SectionItemsManager = ({ section, onItemsChange }) => {
  const typeConfig = getSectionType(section.type);
  const itemsConfig = typeConfig.items || {};
  const items = section.items || [];

  const [editing, setEditing] = useState(null); // { item, isNew }
  const [draft, setDraft] = useState(emptyItem());
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [reordering, setReordering] = useState(false);

  const fields = useMemo(() => itemsConfig.fields || ['title', 'description'], [itemsConfig.fields]);
  const singular = itemsConfig.label || 'Item';
  const plural = itemsConfig.plural || `${singular}s`;

  const openCreate = () => {
    setDraft({ ...emptyItem(), sortOrder: items.length });
    setEditing({ item: null, isNew: true });
  };

  const openEdit = (item) => {
    setDraft({ ...item });
    setEditing({ item, isNew: false });
  };

  const handleSave = async () => {
    const primary = itemsConfig.primary || 'title';
    // Guard against blank rows: an item needs at least its headline field or an image.
    if (!String(draft[primary] || '').trim() && !String(draft.image || '').trim() && !String(draft.value || '').trim()) {
      toast.error(`${getItemFieldLabel(section.type, primary)} is required`);
      return;
    }

    setSaving(true);
    try {
      if (editing.isNew) {
        const response = await sectionItemService.create(section.id, draft);
        onItemsChange([...items, response.data]);
        toast.success(`${singular} added`);
      } else {
        const response = await sectionItemService.update(editing.item.id, draft);
        onItemsChange(items.map((item) => (item.id === editing.item.id ? response.data : item)));
        toast.success(`${singular} updated`);
      }
      setEditing(null);
    } catch (error) {
      toast.error(getApiError(error, `Failed to save ${singular.toLowerCase()}`));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await sectionItemService.delete(deleteTarget.id);
      onItemsChange(items.filter((item) => item.id !== deleteTarget.id));
      toast.success(`${singular} deleted`);
    } catch (error) {
      toast.error(getApiError(error, `Failed to delete ${singular.toLowerCase()}`));
    } finally {
      setDeleting(false);
      setDeleteTarget(null);
    }
  };

  const handleToggle = async (item) => {
    const optimistic = items.map((row) => (row.id === item.id ? { ...row, isActive: !row.isActive } : row));
    onItemsChange(optimistic);
    try {
      await sectionItemService.toggleStatus(item.id);
    } catch (error) {
      onItemsChange(items);
      toast.error(getApiError(error, 'Failed to update status'));
    }
  };

  const persistOrder = async (next) => {
    const previous = items;
    onItemsChange(next.map((item, index) => ({ ...item, sortOrder: index })));
    setReordering(true);
    try {
      await sectionItemService.reorder(section.id, next.map((item) => item.id));
    } catch (error) {
      onItemsChange(previous);
      toast.error(getApiError(error, 'Failed to save the new order'));
    } finally {
      setReordering(false);
    }
  };

  const { move, getItemProps, getItemClass } = useSortable(items, persistOrder);

  const previewText = (item) => {
    const parts = [];
    if (itemsConfig.badge && item[itemsConfig.badge]) parts.push(item[itemsConfig.badge]);
    const description = item.description || item.subtitle || '';
    if (description) parts.push(truncate(stripHtml(description), 70));
    return parts.join(' · ');
  };

  const itemTitle = (item, index) =>
    item[itemsConfig.primary || 'title'] || item.title || item.value || `${singular} ${index + 1}`;

  return (
    <div className="items-manager">
      <div className="im-header">
        <div className="im-heading">
          <h4>{plural}</h4>
          <Badge variant="default" size="small">{items.length}</Badge>
        </div>
        <Button size="small" variant="outline" icon={<FiPlus />} onClick={openCreate}>
          Add {singular}
        </Button>
      </div>

      {items.length === 0 ? (
        <div className="im-empty">
          <FiInbox />
          <p>No {plural.toLowerCase()} yet.</p>
          <Button size="small" variant="ghost" icon={<FiPlus />} onClick={openCreate}>
            Add the first {singular.toLowerCase()}
          </Button>
        </div>
      ) : (
        <ul className={`im-list ${reordering ? 'busy' : ''}`}>
          {items.map((item, index) => (
            <li
              key={item.id}
              className={`im-row ${getItemClass(index)} ${item.isActive ? '' : 'inactive'}`}
              {...getItemProps(index)}
            >
              <span className="im-handle" title="Drag to reorder"><FiMove /></span>

              <span className="im-thumb">
                {item.image ? (
                  <img src={item.image} alt="" loading="lazy" />
                ) : item.icon ? (
                  <span className="im-thumb-icon">{renderFaIcon(item.icon)}</span>
                ) : item.value ? (
                  <span className="im-thumb-value">{truncate(item.value, 5)}</span>
                ) : (
                  <span className="im-thumb-icon muted"><FiImage /></span>
                )}
              </span>

              <span className="im-text">
                <span className="im-title">{itemTitle(item, index)}</span>
                {previewText(item) && <span className="im-sub">{previewText(item)}</span>}
              </span>

              <span className="im-actions">
                <button type="button" className="im-btn" title="Move up" onClick={() => move(index, index - 1)} disabled={index === 0}>
                  <FiChevronUp />
                </button>
                <button type="button" className="im-btn" title="Move down" onClick={() => move(index, index + 1)} disabled={index === items.length - 1}>
                  <FiChevronDown />
                </button>
                <button
                  type="button"
                  className={`im-btn ${item.isActive ? 'on' : ''}`}
                  title={item.isActive ? 'Published — click to hide' : 'Hidden — click to publish'}
                  onClick={() => handleToggle(item)}
                >
                  {item.isActive ? <FiEye /> : <FiEyeOff />}
                </button>
                <button type="button" className="im-btn" title="Edit" onClick={() => openEdit(item)}>
                  <FiEdit2 />
                </button>
                <button type="button" className="im-btn danger" title="Delete" onClick={() => setDeleteTarget(item)}>
                  <FiTrash2 />
                </button>
              </span>
            </li>
          ))}
        </ul>
      )}

      <Modal
        isOpen={Boolean(editing)}
        onClose={() => setEditing(null)}
        title={editing?.isNew ? `Add ${singular}` : `Edit ${singular}`}
        size="large"
        footer={
          <>
            <Button variant="ghost" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={handleSave} loading={saving}>{editing?.isNew ? `Add ${singular}` : 'Save changes'}</Button>
          </>
        }
      >
        <div className="im-form">
          {fields.map((field) => {
            const config = ITEM_FIELDS[field] || { type: 'text' };
            return (
              <CmsField
                key={field}
                type={config.type}
                label={getItemFieldLabel(section.type, field)}
                placeholder={config.placeholder}
                value={draft[field]}
                onChange={(value) => setDraft((prev) => ({ ...prev, [field]: value }))}
                rows={3}
              />
            );
          })}

          <div className="im-form-footer">
            <Toggle
              name="item-status"
              checked={draft.isActive}
              onChange={(e) => setDraft((prev) => ({ ...prev, isActive: e.target.checked }))}
              label={draft.isActive ? 'Published' : 'Hidden'}
            />
          </div>
        </div>
      </Modal>

      <ConfirmDialog
        isOpen={Boolean(deleteTarget)}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title={`Delete ${singular}`}
        message={`Delete "${deleteTarget ? itemTitle(deleteTarget, 0) : ''}"? This cannot be undone.`}
        confirmText="Delete"
        loading={deleting}
      />
    </div>
  );
};

export default SectionItemsManager;
